import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { PartnerAccountProps } from './types';
import { styles } from './styles';
import { Button } from '@v2/components';
import { DeleteConfirmationModal } from '@v2/features';
import { useRemoveAccountFromPartner } from '@v2/hooks';

const PartnerAccount: React.FC<PartnerAccountProps> = ({
  account,
  partnerId,
  onRemoved,
}) => {
  const history = useHistory();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { removeAccountFromPartner, loading: removeLoading } =
    useRemoveAccountFromPartner();

  const handleClick = () => {
    history.push(`/admin/accounts?accountId=${account.id}`);
  };

  const handleConfirmDelete = async () => {
    const success = await removeAccountFromPartner(partnerId, account.id);
    setConfirmOpen(false);
    if (success) {
      toast.success('Account removed from partner.');
      onRemoved();
    } else {
      toast.error('Failed to remove account from partner.');
    }
  };

  const displayName = `${account.firstName} ${account.lastName}`.trim() || account.email;

  return (
    <>
      <div className={styles.container}>
        <div
          role="button"
          tabIndex={0}
          onClick={handleClick}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleClick();
            }
          }}
          className={styles.main}
        >
          <div className={styles.info}>
            <span className={styles.name}>
              {account.firstName} {account.lastName}
            </span>
            <span className={styles.email}>{account.email}</span>
            <span className={styles.role}>{account.role}</span>
          </div>
        </div>
        <Button
          type="button"
          variant="ghost-danger"
          size="sm"
          className="tw-shrink-0 tw-self-start"
          onClick={(e) => {
            e.stopPropagation();
            setConfirmOpen(true);
          }}
          aria-label={`Remove ${displayName} from partner`}
          icon={<FontAwesomeIcon icon={faTrash} />}
        />
      </div>

      <DeleteConfirmationModal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        isDeleting={removeLoading}
      />
    </>
  );
};

export default PartnerAccount;
